// src/app/auth/auth.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { SessionTimeoutService } from '../services/session-timeout.service';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

let redirecting = false;

function toOrigin(url: string): string | null {
  try { return new URL(url).origin; } catch { return null; }
}

// Build a fast lookup set of allowed origins (normalized to origins)
const allowedOriginSet = new Set<string>(
  (environment.allowedOrigins ?? [])
    .map(toOrigin)
    .filter((o): o is string => !!o)
);

// Always include the page’s own origin (helps when you use relative URLs)
allowedOriginSet.add(window.location.origin);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const sessionTimeout = inject(SessionTimeoutService);

  // Determine the origin of this request (handles relative URLs too)
  let requestOrigin: string | null = null;
  try {
    requestOrigin = new URL(req.url, window.location.origin).origin;
  } catch {
    requestOrigin = null;
  }

  const shouldSendCreds = !!requestOrigin && allowedOriginSet.has(requestOrigin);

  const maybeWithCreds = shouldSendCreds
    ? req.clone({
      withCredentials: true,
      setHeaders: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    : req;

  sessionTimeout.manualReset?.();

  return next(maybeWithCreds).pipe(
    catchError((err: HttpErrorResponse) => {
      // Ignore OPTIONS/preflight
      if (maybeWithCreds.method === 'OPTIONS') {
        return throwError(() => err);
      }

      if (err.status === 401) {
        if (!redirecting && !router.url.startsWith('/login')) {
          redirecting = true;
          const returnUrl = window.location.pathname + window.location.search;
          setTimeout(() => {
            router.navigate(['/login'], { queryParams: { returnUrl } })
              .finally(() => (redirecting = false));
          }, 0);
        }
      }
      // Let 403 be handled by guards or components as you prefer
      return throwError(() => err);
    })
  );
};
